import { useState } from 'react'
import { Link } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import { PageContainer } from '@/components/PageContainer'
import { PageHeader } from '@/components/PageHeader'
import { FlashBanner } from '@/components/FlashBanner'
import { Button } from '@/components/ui/button'
import type { ProjectDetail } from '@/types/project'
import { EllipsisVerticalIcon } from 'lucide-react'
import { ProcessStepCard } from './components/ProcessStepCard'
import { ScoreArc } from '@/pages/requirements_quality/components/ScoreArc'
import { StatBar } from '@/pages/requirements_quality/components/StatBar'
import { CATEGORY_ITEMS } from '@/pages/requirements_quality/components/CategorySection'
import { VOLUME_ITEMS, RISK_LEVEL_META } from '@/pages/requirements_quality/components/scoreItems'

interface Props {
  project_detail: ProjectDetail
}

const STEP_ICONS = ['📋', '📐', '🔍']
const STEP_DESCRIPTIONS = [
  'ヒアリングノートを登録して構造化',
  '概念モデル・要求・要件・業務フローを確認・修正',
  '矛盾・抜け漏れのリスクスコアを確認'
]

export default function ProjectsShow ({ project_detail: project }: Props): React.ReactElement {
  const [menuOpen, setMenuOpen] = useState(false)

  const rd = project.requirements_specification
  const rdBase = `/projects/${project.id}`
  const rdPipelineStatus = rd?.pipeline_status ?? 'idle'
  const analyzing = rdPipelineStatus === 'running' || rdPipelineStatus === 'queued'
  const rdReady = rdPipelineStatus === 'succeeded'

  const riskSummary = project.risk_summary
  const artifactVolume = riskSummary?.artifact_volume
  const categoryBreakdown = riskSummary?.category_breakdown
  const volumeMax = (artifactVolume != null) ? Math.max(...VOLUME_ITEMS.map(i => artifactVolume[i.key]), 1) : 1
  const categoryMax = (categoryBreakdown != null) ? Math.max(...CATEGORY_ITEMS.map(i => categoryBreakdown[i.key]), 1) : 1

  const requirementsSteps = [
    {
      title: '要件ヒアリング',
      href: `${rdBase}/source_notes`,
      done: rdPipelineStatus !== 'idle' && !analyzing,
      disabled: false
    },
    {
      title: '要件定義',
      href: `${rdBase}/requirements_specification`,
      done: rdReady,
      disabled: !(analyzing || rdPipelineStatus !== 'idle')
    },
    {
      title: '要件品質チェック',
      href: `${rdBase}/requirements_quality`,
      done: rdReady,
      disabled: analyzing || rdPipelineStatus === 'idle'
    }
  ]

  return (
    <AppLayout project={project}>
      <PageContainer>
        <PageHeader
          title={project.name}
          subtitle={project.industry?.label}
          backHref='/projects'
          backLabel='プロジェクト一覧'
        >
          <div style={{ position: 'relative' }}>
            <Button
              variant='outline'
              onClick={() => setMenuOpen((v) => !v)}
              onBlur={() => setTimeout(() => setMenuOpen(false), 150)}
            >
              <EllipsisVerticalIcon width={16} height={16} />メニュー
            </Button>
            {menuOpen && (
              <div className='absolute right-0 top-full mt-1 bg-(--bp-surface) border border-(--bp-border) rounded-lg shadow-[0_4px_16px_rgba(26,35,56,0.12)] z-[100] min-w-[160px]'>
                <Link
                  href={`/projects/${project.id}/edit`}
                  className='block px-4 py-2.5 text-[13px] text-(--bp-ink) no-underline hover:bg-(--bp-surface2)'
                >
                  プロジェクト設定
                </Link>
                <Link
                  href={`/projects/${project.id}/memberships`}
                  className='block px-4 py-2.5 text-[13px] text-(--bp-ink) no-underline hover:bg-(--bp-surface2)'
                >
                  メンバー管理
                </Link>
              </div>
            )}
          </div>
        </PageHeader>

        <FlashBanner />

        <div className='text-[12px] font-semibold text-(--bp-ink3) tracking-[0.04em] uppercase mb-3'>
          要件定義プロセス
        </div>
        <div className='grid grid-cols-3 gap-3 mt-2'>
          {requirementsSteps.map((step, i) => (
            <ProcessStepCard
              key={i}
              num={i + 1}
              icon={STEP_ICONS[i]}
              title={step.title}
              description={STEP_DESCRIPTIONS[i]}
              href={step.href}
              done={step.done}
              disabled={step.disabled}
            />
          ))}
        </div>

        {(riskSummary != null) && (
          <div className='mt-7'>
            <div className='flex items-center justify-between mb-3'>
              <div className='text-[12px] font-semibold text-(--bp-ink3) tracking-[0.04em] uppercase'>
                最新の品質チェック結果
              </div>
              {riskSummary.critical_count > 0 && (
                <span className='flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-(--bp-critical-pale) border border-(--bp-critical-border) text-[11px] font-bold text-destructive'>
                  CRITICAL {riskSummary.critical_count}
                </span>
              )}
            </div>
            <div className='bg-(--bp-panel-bg) text-(--bp-panel-ink) rounded-[10px]'>
              <div className='p-6 flex gap-8 items-center flex-wrap'>
                <div className='flex flex-col items-center shrink-0'>
                  <div className='text-[10px] font-bold tracking-[.1em] uppercase text-(--bp-panel-ink2) mb-2'>
                    要件品質スコア
                  </div>
                  <ScoreArc score={riskSummary.total_score} />
                  <div
                    className='text-[11px] font-bold tracking-[.06em] uppercase mt-1.5 mb-3'
                    style={{ color: RISK_LEVEL_META[riskSummary.level].color }}
                  >
                    {RISK_LEVEL_META[riskSummary.level].label}
                  </div>
                  <Button asChild className='shrink-0'>
                    <Link href={`/projects/${project.id}/requirements_quality`}>
                      詳細を確認 →
                    </Link>
                  </Button>
                </div>
                <div className='flex-1 min-w-65 flex flex-col gap-4'>
                  <div>
                    <div className='text-[10px] font-bold tracking-[.06em] text-(--bp-panel-ink2) mb-2'>
                      成果物の作り込み（{VOLUME_ITEMS.reduce((sum, i) => sum + riskSummary.artifact_volume[i.key], 0)}要素）
                    </div>
                    <div className='grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-3'>
                      {VOLUME_ITEMS.map(item => (
                        <StatBar
                          key={item.key}
                          label={item.label}
                          value={riskSummary.artifact_volume[item.key]}
                          max={volumeMax}
                          color='var(--bp-accent)'
                        />
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className='text-[10px] font-bold tracking-[.06em] text-(--bp-panel-ink2) mb-2'>
                      リスクの内訳（{riskSummary.critical_count + riskSummary.warning_count}件）
                    </div>
                    <div className='grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-3'>
                      {CATEGORY_ITEMS.map(item => (
                        <StatBar
                          key={item.key}
                          label={item.label}
                          value={riskSummary.category_breakdown[item.key]}
                          max={categoryMax}
                          color='var(--bp-critical)'
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </PageContainer>
    </AppLayout>
  )
}
